import { splitFullName } from "../components/MyPerformance/myPerformanceHelpers";
import { getBaseUrl } from "../redux/config";
import {
  getAuthApiHeaders,
  getClientAuth,
  getIncomingDisplayName,
  isDelegatedSession,
  secureFetch,
  setIncomingDisplayName,
} from "./common";

export { setIncomingDisplayName, getIncomingDisplayName };

/** @typedef {{ fullName: string, firstName: string, lastName: string, initials: string, isDelegated: boolean, isLoading: boolean }} DisplayIdentity */

export const DISPLAY_IDENTITY_LOADING = {
  fullName: "",
  firstName: "",
  lastName: "",
  initials: "",
  isDelegated: false,
  isLoading: true,
};

export function formatEmailAsDisplayName(email = "") {
  const local = String(email || "").split("@")[0] || "";
  return local.replace(/[._-]+/g, " ").trim();
}

/**
 * @param {string} fullName
 * @returns {Omit<DisplayIdentity, "isDelegated">}
 */
export function parsePersonName(fullName = "") {
  const full = String(fullName || "").trim();
  const [firstName, lastName] = splitFullName(full);
  const initials = `${firstName?.[0] || ""}${lastName?.[0] || firstName?.[1] || ""}`.toUpperCase();
  return { fullName: full, firstName, lastName, initials };
}

/**
 * Who to show in avatars / greetings: logged-in user when delegated, else rep/outgoing name.
 * @param {string} [repNameFromStore] Name from /rep_info (outgoing territory user).
 * @param {{ repInfoPending?: boolean, incomingPending?: boolean }} [options]
 * @returns {DisplayIdentity}
 */
export function getDisplayIdentity(repNameFromStore = "", options = {}) {
  const { repInfoPending = false, incomingPending = false } = options;
  const delegated = isDelegatedSession();

  if (repInfoPending || incomingPending) {
    return { ...DISPLAY_IDENTITY_LOADING, isDelegated: delegated };
  }

  const { incomingEmail } = getClientAuth();
  const incomingName = getIncomingDisplayName();
  const repName = String(repNameFromStore || "").trim();

  let fullName = "";
  if (delegated) {
    fullName = incomingName || formatEmailAsDisplayName(incomingEmail);
  } else if (repName) {
    fullName = repName;
  } else if (incomingName) {
    fullName = incomingName;
  } else {
    fullName = formatEmailAsDisplayName(incomingEmail);
  }

  return { ...parsePersonName(fullName), isDelegated: delegated, isLoading: false };
}

/** Fetch display name from API when not already cached (e.g. delegated session). */
export async function fetchAndPersistIncomingDisplayName() {
  const response = await secureFetch(`${getBaseUrl()}/api/user-info`, {
    headers: {
      "Content-Type": "application/json",
      "use-case": "src",
      ...getAuthApiHeaders(),
      client: "UT",
    },
  });
  if (!response.ok) {
    throw new Error(`user-info failed: ${response.status}`);
  }
  const data = await response.json();
  const name = data?.name || data?.preferred_username || "";
  setIncomingDisplayName(name);
  return name;
}
